<template>
  <div
    ref="baseLoaderWrapper"
    class="base-loader-wrapper"
  >
    <div
      v-show="showLoader"
      ref="baseLoader"
      class="base-loader"
    >
      <i class="fa-solid fa-spinner fa-spin-pulse fa-spin-reverse" />
    </div>
    <slot />
  </div>
</template>

<script lang="ts" setup>
import {
  onMounted, onBeforeUnmount, computed, ref, watch, useSlots,
} from 'vue';

const props = defineProps<{
  show?: boolean;
  disabled?: boolean;
}>();

const emit = defineEmits(['update', 'showLoader', 'hideLoader']);

const slots = useSlots();
const hasSlotDefault = ref(typeof slots.default === 'function');
const slotDefault = ref(hasSlotDefault.value ? slots.default!() : undefined);
const contentElement = ref<Element | null>(null);
const contentStyle = ref<CSSStyleDeclaration | null>(null);

const baseLoader = ref<HTMLElement | null>(null);
const baseLoaderWrapper = ref<HTMLElement | null>(null);

const loaders = ref<any[]>([]);
const showLoader = computed<boolean>(() => (props.disabled ? false : loaders.value.length > 0));
watch(() => showLoader.value, (value) => {
  if (value) {
    emit('showLoader');
  } else {
    emit('hideLoader');
  }
  emit('update', value);
});

onMounted(() => {
  if (!hasSlotDefault.value) {
    return;
  }

  if (hasSlotDefault.value && slotDefault.value!.length > 1) {
    console.warn('BaseLoader has more than one child element. This is not allowed. Please wrap your content in a single element.');
  }

  if (hasSlotDefault.value && baseLoaderWrapper.value) {
    const currentPosition = baseLoaderWrapper.value!.style.position;
    if (currentPosition === '' || currentPosition === 'static') {
      baseLoaderWrapper.value!.style.position = 'relative';
    }
  }

  contentElement.value = slotDefault.value![0].elm as Element;
  contentStyle.value = window.getComputedStyle(contentElement.value);
});

const updateStyle = () => {
  if (baseLoader.value && contentStyle.value) {
    baseLoader.value.style.overflow = contentStyle.value.overflow;
    baseLoader.value.style.borderRadius = contentStyle.value.borderRadius;
  }
};

const addLoader = () => {
  loaders.value.push(new Date());

  updateStyle();
};
window.addEventListener('showLoader', addLoader);

const popLoader = () => {
  loaders.value.pop();
};
window.addEventListener('hideLoader', popLoader);

onBeforeUnmount(() => {
  window.removeEventListener('showLoader', addLoader);
  window.removeEventListener('hideLoader', popLoader);
});

watch(() => props.show, (value) => {
  if (value) {
    addLoader();
  } else {
    popLoader();
  }
});
</script>

<style lang="scss" scoped>
.base-loader-wrapper {
  padding: 0 !important;
}

.base-loader {
  position: absolute;
  background-color: rgba(255,255,255, .67);
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(1px);
  top: 0;
  left: 0;
  font-size: 3rem;
  z-index: 1000;
}
</style>
